import {Button, Form} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import React, {
    ForwardedRef,
    forwardRef, ReactElement, ReactNode, useImperativeHandle, useState,
} from 'react';

import {CustomCard} from 'components/@common/widgets/custom-card';
import {selectContextRawData} from 'modules/context/context-selector';
import {TEntity} from 'modules/data';
import {useAfterEffect} from 'shared/hooks/use-after-effect';
import {TStateSetter} from 'shared/types/generics';
import {useAppSelector} from 'store/config/hooks';

import './parameters-filter.less';

interface IParametersOnClearCallbackArgs {
    setIsFilterApplied: TStateSetter<boolean>;
}

interface IParametersFilterProps<FormValues> {
    onChange: (values?: FormValues) => void;
    onClear?: (args: IParametersOnClearCallbackArgs) => void;
    isEmpty?: boolean;
    children: ((props: {isFilterApplied: boolean}) => ReactElement);
    isFilterAppliedByDefault?: boolean;
    className?: cn.Argument;
    changeFilterParametersText?: string;
    clearFilterParametersText?: string;
    clearFilterParametersIcon?: ReactNode;
}

export interface IParametersFilterRef<FormValues> {
    setInitialFormValues: TStateSetter<FormValues | undefined>;
    setFieldValue: (field: string, value: TEntity | undefined) => void;
}

export const ParametersFilterRaw = <FormValues extends object>(
    {
        onChange,
        onClear,
        isEmpty,
        children: Children,
        isFilterAppliedByDefault,
        className,
        changeFilterParametersText = 'Скорректировать параметры отчёта',
        clearFilterParametersText = 'Очистить',
        clearFilterParametersIcon,
    }: IParametersFilterProps<FormValues>,
    ref: ForwardedRef<IParametersFilterRef<FormValues>>,
) => {
    const [form] = useForm();

    const [isFilterApplied, setIsFilterApplied] = useState(!!isFilterAppliedByDefault);
    const [initialFormValues, setInitialFormValues] = useState<FormValues>();
    const {organizationId, taxPeriodId} = useAppSelector(selectContextRawData) ?? {};

    const handleFinish = (values: FormValues) => {
        setInitialFormValues(values);
    };

    useImperativeHandle(ref, () => ({
        setInitialFormValues,
        setFieldValue: (field: string, value: TEntity | undefined) => {
            form.setFieldsValue({[field]: value});
        },
    }));

    useAfterEffect(() => {
        onChange(initialFormValues);
        setIsFilterApplied(true);
        form.resetFields();
    }, [initialFormValues, organizationId, taxPeriodId]);

    return (
        <CustomCard
            title="Параметры отчёта"
            className={cn('parameters-filter')}
            titleClassName={cn('parameters-filter__title')}
        >
            <div>
                <Form<FormValues>
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                    initialValues={initialFormValues}
                >
                    <div className={cn('parameters-filter__filters', className)}>
                        {!isEmpty ? <Children isFilterApplied={isFilterApplied} /> : 'Нет доступных фильтров'}
                    </div>
                </Form>
                <div className={cn('parameters-filter__buttons')}>
                    {isFilterApplied ? (
                        <Button
                            type="primary"
                            onClick={() => {
                                setIsFilterApplied(false);
                            }}
                        >{changeFilterParametersText}
                        </Button>
                    ) : (
                        <>
                            <Button
                                disabled={isEmpty}
                                type="primary"
                                onClick={() => {
                                    form.submit();
                                }}
                            >Применить
                            </Button>
                            <Button
                                disabled={isEmpty}
                                type="default"
                                onClick={() => {
                                    onClear?.({
                                        setIsFilterApplied,
                                    });
                                    setInitialFormValues(undefined);
                                    setTimeout(() => {
                                        form.resetFields();
                                    }, 0);
                                }}
                                icon={clearFilterParametersIcon}
                            >{clearFilterParametersText}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </CustomCard>
    );
};

export const ParametersFilter = forwardRef(ParametersFilterRaw) as <FormValues extends object>(
    props: IParametersFilterProps<FormValues> & { ref?: ForwardedRef<IParametersFilterRef<FormValues>> }
  ) => ReturnType<typeof ParametersFilterRaw>;
